(function (window, document) {
  'use strict';
  if (window.HLXTracking && window.HLXTracking.version === 2) return;

  const namespaces = ['home', 'short_product', 'short_conversion', 'checkout', 'checkout_thanks'];
  const accountPattern = /^AW-[1-9]\d*$/;
  const labelPattern = /^[A-Za-z0-9_-]{1,128}$/;
  const configured = new Set();
  const sent = new Set();
  let sequence = 0;
  let config = null;
  let ready = false;
  // Owner rule (2026-09-26; the client's legal department confirmed it and the regulamin states it):
  // submitting a short form with the ticked terms checkbox is consent. It overwrites the banner choice.
  const formConsentStores = ['onebrandska', 'eu_hlp'];
  // Lead conversion value as in the store's pre-11.09 theme (EU sets its own value at build time).
  const leadValues = { onebrandska: 1 };
  let formConsentGranted = false;

  function createSubmissionId() {
    try {
      if (typeof window.crypto?.randomUUID === 'function') return 'hlx-' + window.crypto.randomUUID();
    } catch (_) {}
    sequence += 1;
    return 'hlx-' + Date.now().toString(36) + '-' + sequence.toString(36) + '-' + Math.random().toString(36).slice(2);
  }

  function googleSettings(namespace) {
    const value = config?.conversion?.[namespace];
    if (!namespaces.includes(namespace) || value?.google !== true) return null;
    if (!accountPattern.test(value.tagAccountId || '')) return null;
    if (typeof value.tagLabel !== 'string' || (value.tagLabel !== '' && !labelPattern.test(value.tagLabel))) return null;
    if (namespace !== 'home' && !labelPattern.test(value.tagLabel)) return null;
    if (typeof value.tagCurrency !== 'string' || (value.tagCurrency !== '' && !/^[A-Z]{3}$/.test(value.tagCurrency))) return null;
    return value;
  }

  function grantFormConsent(done) {
    formConsentGranted = true;
    try {
      if (typeof window.gtag === 'function') window.gtag('consent', 'update', { ad_storage: 'granted',
        analytics_storage: 'granted', ad_user_data: 'granted', ad_personalization: 'granted',
        functionality_storage: 'granted', personalization_storage: 'granted' });
    } catch (_) {}
    const consent = { analytics: true, marketing: true, preferences: true, sale_of_data: true };
    const apply = () => {
      try { window.Shopify.customerPrivacy.setTrackingConsent(consent, error => { if (!error && done) done(); }); } catch (_) {}
    };
    try {
      if (typeof window.Shopify?.customerPrivacy?.setTrackingConsent === 'function') apply();
      else window.Shopify?.loadFeatures?.([{ name: 'consent-tracking-api', version: '0.1' }], error => { if (!error) apply(); });
    } catch (_) {}
  }

  // Lets a form handler store the checkbox consent before it hashes the phone (EU hashes in its own adapter).
  function formConsentGrant() {
    if (!formConsentStores.includes(config?.storeKey)) return Promise.resolve(false);
    return new Promise(resolve => {
      const timer = setTimeout(() => resolve(true), 2500);
      grantFormConsent(() => { clearTimeout(timer); resolve(true); });
    });
  }

  function enhancedDataAllowed() {
    if (formConsentGranted) return true;
    if (config?.storeKey === 'eu_hlp') return window.HLXConsent?.enhancedDataAllowed?.() === true;
    const privacy = window.Shopify?.customerPrivacy;
    if (typeof privacy?.marketingAllowed === 'function' && privacy.marketingAllowed() !== true) return false;
    let consent;
    for (const item of window.dataLayer || []) {
      if (item?.[0] === 'consent' && ['default', 'update'].includes(item[1]) && item[2]?.ad_user_data) consent = item[2].ad_user_data;
    }
    return consent !== 'denied';
  }

  function conversion(namespace, details) {
    try {
      if (details?.consent === true && formConsentStores.includes(config?.storeKey)) grantFormConsent();
      const settings = googleSettings(namespace);
      if (!ready || !settings || !labelPattern.test(settings.tagLabel) || typeof window.gtag !== 'function') return false;
      if (config?.storeKey === 'eu_hlp' && !formConsentGranted && window.HLXConsent?.marketingAllowed?.() !== true) return false;
      const eventId = typeof details?.eventId === 'string' && /^hlx-[A-Za-z0-9-]+$/.test(details.eventId) ? details.eventId : createSubmissionId();
      const destination = settings.tagAccountId + '/' + settings.tagLabel;
      const key = destination + ':' + eventId;
      if (sent.has(key)) return false;
      sent.add(key);
      const parameters = { send_to: destination, value: 0, transaction_id: eventId };
      if (leadValues[config?.storeKey]) parameters.value = leadValues[config.storeKey];
      if (settings.tagCurrency) parameters.currency = settings.tagCurrency;
      const phone = typeof details?.phone === 'string' && /^\+[1-9]\d{6,14}$/.test(details.phone) ? details.phone : '';
      let enhanced = false;
      try {
        if (phone && enhancedDataAllowed()) {
          window.gtag('set', 'user_data', { phone_number: phone });
          enhanced = true;
        }
      } catch (_) {}
      try {
        window.gtag('event', 'conversion', parameters);
      } finally {
        if (enhanced) {
          try { window.gtag('set', 'user_data', {}); } catch (_) {}
        }
      }
      return true;
    } catch (_) {
      return false;
    }
  }

  window.HLXTracking = Object.freeze({ version: 2, createSubmissionId, conversion, grantFormConsent: formConsentGrant });

  function readConfiguration() {
    try {
      const candidate = JSON.parse(document.getElementById('hlx-tracking-config')?.textContent || 'null');
      if (candidate?.schemaVersion !== 2 || !['multi1', 'multi2', 'eu_hlp', 'onebrandska'].includes(candidate.storeKey)) return null;
      if (typeof candidate.hostname !== 'string' || !/^[a-z0-9.-]+$/.test(candidate.hostname)) return null;
      if (['eu_hlp', 'onebrandska'].includes(candidate.storeKey)) {
        const eu = candidate.storeKey === 'eu_hlp';
        const host = eu ? 'eu.healthlabspharm.com' : 'dobra-oferta.com';
        const shop = eu ? 'healthlabs-pharm.myshopify.com' : 'onebrandska.myshopify.com';
        const countries = eu ? ['AT', 'CY', 'DE', 'GR', 'ES', 'IE', 'IT', 'SK'] : ['PL'];
        if (window.Shopify?.shop !== shop || ![host, shop].includes(window.location?.hostname)) return null;
        if (candidate.hostname !== host || !countries.includes(candidate.countryCode)) return null;
        if (candidate.domainKey !== host + (eu ? '/' + candidate.countryCode.toLowerCase() : '')) return null;
      }
      if (!candidate.conversion || typeof candidate.conversion !== 'object') return null;
      for (const name of namespaces) {
        const settings = candidate.conversion[name];
        if (!settings || ['google', 'bing', 'kelko'].some(key => typeof settings[key] !== 'boolean')) return null;
        if (['tagAccountId', 'tagLabel', 'tagCurrency', 'kelkoInfo'].some(key => typeof settings[key] !== 'string')) return null;
        if (settings.tagAccountId !== '' && !accountPattern.test(settings.tagAccountId)) return null;
        if (settings.tagLabel !== '' && !labelPattern.test(settings.tagLabel)) return null;
        if (settings.tagCurrency !== '' && !/^[A-Z]{3}$/.test(settings.tagCurrency)) return null;
        if (settings.google && (!accountPattern.test(settings.tagAccountId) || (name !== 'home' && !labelPattern.test(settings.tagLabel)))) return null;
      }
      return candidate;
    } catch (_) {
      return null;
    }
  }

  function googleLoader() {
    return Array.from(document.scripts || []).find(script => {
      try {
        const url = new URL(script.src);
        return url.protocol === 'https:' && url.hostname === 'www.googletagmanager.com' && url.pathname === '/gtag/js' && /^(?:AW-[1-9]\d*|G-[A-Z0-9]+|GT-[A-Z0-9]+)$/.test(url.searchParams.get('id') || '') && [null, 'dataLayer'].includes(url.searchParams.get('l'));
      } catch (_) {
        return false;
      }
    });
  }

  function initializeGoogle() {
    const accounts = [...new Set(namespaces.map(name => googleSettings(name)?.tagAccountId).filter(Boolean))];
    if (!accounts.length) return;
    if (window.dataLayer && !Array.isArray(window.dataLayer)) return;
    window.dataLayer = window.dataLayer || [];
    for (const command of window.dataLayer) {
      if (command?.[0] === 'config' && accountPattern.test(command[1] || '')) configured.add(command[1]);
    }
    if (typeof window.gtag !== 'function') {
      window.gtag = function () { window.dataLayer.push(arguments); };
    }
    // Every store loads the tag at page start from its domain config (owner decision 2026-09-26), so a lead is
    // sent at once when the form is submitted. On EU the hlx-google-consent baseline denies storage until consent.
    loadGoogle(accounts);
    ready = true;
  }

  function loadGoogle(accounts) {
    const existingLoader = googleLoader();
    if (!existingLoader) {
      const script = document.createElement('script');
      script.async = true;
      script.src = 'https://www.googletagmanager.com/gtag/js?id=' + encodeURIComponent(accounts[0]);
      script.dataset.hlxTracking = 'google';
      document.head.appendChild(script);
    }
    if (!window.dataLayer.some(command => command?.[0] === 'js') && !existingLoader) window.gtag('js', new Date());
    for (const account of accounts) {
      if (!configured.has(account)) {
        window.gtag('config', account, { send_page_view: false });
        configured.add(account);
      }
    }
  }

  config = readConfiguration();
  try { initializeGoogle(); } catch (_) {}
  // Owner rule: an order placed in checkout is consent too. The checkout pixel leaves this one-shot flag;
  // apply it to the banner here, then clear it once Shopify has stored the choice.
  try {
    if (formConsentStores.includes(config?.storeKey) && /(?:^|;\s*)_hlx_order_consent=1(?:;|$)/.test(document.cookie || '')) {
      grantFormConsent(() => { document.cookie = '_hlx_order_consent=; path=/; max-age=0; samesite=lax'; });
    }
  } catch (_) {}
})(window, document);
